import {useState, useEffect, useRef} from 'react'
import {toast} from 'react-toastify'
import {useLocation} from 'react-router-dom'
import {getPlatformFieldDataAttributesAsync} from 'src/redux/actions/templateAndPricingActions'
import {useAppDispatch, useAppSelector} from 'src/redux/storeHooks'
import {PlatformDataType} from 'src/interfaces/Platform.interface'

export const useAttributePolling = (
  selectedSecondaryPlatform: PlatformDataType | null
) => {
  const dispatch = useAppDispatch()
  const location = useLocation()
  const {getAttributeDataLoading} = useAppSelector(
    (state) => state.templateAndPricing
  )

  const [isAttributePolling, setIsAttributePolling] = useState(false)
  const [showAttributes, setShowAttributes] = useState(false)
  const attributePollingTimerRef = useRef<NodeJS.Timeout | null>(null)
  const attributePollingCategoryRef = useRef<number | null>(null)
  const attributePollingAttemptsRef = useRef(0)

  const isAttributeLoading = getAttributeDataLoading || isAttributePolling

  const clearAttributePolling = () => {
    if (attributePollingTimerRef.current) {
      clearTimeout(attributePollingTimerRef.current)
    }
    attributePollingTimerRef.current = null
    attributePollingAttemptsRef.current = 0
    attributePollingCategoryRef.current = null
    setIsAttributePolling(false)
  }

  const isAttributePayloadReady = (payload: any) => Array.isArray(payload)

  const shouldContinueAttributePolling = (payload: any) =>
    payload && typeof payload === 'object' && payload.queue_id

  const pollAttributesForCategory = async (
    categoryId: number,
    attempt: number
  ) => {
    const platformName =
      selectedSecondaryPlatform?.display_name ||
      selectedSecondaryPlatform?.name ||
      selectedSecondaryPlatform?.platform_info?.display_name ||
      selectedSecondaryPlatform?.platform_info?.name ||
      ''

    const shouldPassPlatformId =
      (location.pathname === '/listing-profiles' ||
        location.pathname === '/listing-profiles-old' ||
        location.pathname === '/listing-profiles-previous') &&
      /walmart/i.test(platformName) &&
      Boolean(selectedSecondaryPlatform?.id)

    const response = await dispatch(
      getPlatformFieldDataAttributesAsync({
        category_id: categoryId,
        ...(shouldPassPlatformId 
          ? {platform_id: selectedSecondaryPlatform?.id} 
          : {}),
      })
    )

    if (attributePollingCategoryRef.current !== categoryId) return

    const payload = response?.payload

    if (isAttributePayloadReady(payload)) {
      setShowAttributes(true)
      clearAttributePolling()
      return
    }

    if (shouldContinueAttributePolling(payload)) {
      if (attempt >= 50) {
        toast.error('Timeout fetching Platform Attributes. Please try again.')
        setShowAttributes(false)
        clearAttributePolling()
        return
      }
      attributePollingAttemptsRef.current = attempt + 1
      attributePollingTimerRef.current = setTimeout(
        () => pollAttributesForCategory(categoryId, attempt + 1),
        5000
      )
      return
    }

    // Default failure case: stay hidden and clear
    setShowAttributes(false)
    clearAttributePolling()
  }

  const fetchAttributesForCategory = (categoryId: number | null) => {
    if (categoryId === null) return
    clearAttributePolling()
    setShowAttributes(false)
    setIsAttributePolling(true)
    attributePollingCategoryRef.current = categoryId
    pollAttributesForCategory(categoryId, 0)
  }

  useEffect(() => {
    return () => {
      clearAttributePolling()
    }
  }, [])

  return {
    isAttributeLoading,
    showAttributes,
    setShowAttributes,
    fetchAttributesForCategory,
    clearPolling: clearAttributePolling,
  }
}
